import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import config from "../coreFIles/config";
import { adminInfoVideoCreateAction, adminInfoVideoGetAction, adminInfoVideoUpdateAction } from "../Action/action";

const TYPE_OPTIONS = [
  { value: "Selfhosted", label: "Selfhosted" },
  { value: "Thirdparty", label: "Thirdparty" }
];

const VIDEO_ORIENTATION_OPTIONS = [
  { value: "landscape", label: "Landscape" },
  { value: "portrait", label: "Portrait" }
];

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

export default function InfoVideoForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [title, setTitle] = useState("");
  const [type, setType] = useState("Selfhosted");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoOrientation, setVideoOrientation] = useState("landscape");
  const [active, setActive] = useState(1);

  const [existingVideoUrl, setExistingVideoUrl] = useState("");
  const [existingThumbUrl, setExistingThumbUrl] = useState("");

  const videoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState("");

  useEffect(() => {
    if (!thumbFile) {
      if (thumbPreviewUrl) setThumbPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(thumbFile);
    setThumbPreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbFile]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEdit) return;
      setLoading(true);
      setError("");
      try {
        const res = await adminInfoVideoGetAction(id);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load info video.");
          return;
        }
        const d = res.data || {};
        setTitle(d.title || "");
        setType(d.type === "Thirdparty" ? "Thirdparty" : "Selfhosted");
        setVideoUrl(d.video_url || "");
        setVideoOrientation(d.video_orientation === "portrait" ? "portrait" : "landscape");
        setExistingVideoUrl(d.video_url ? String(d.video_url) : "");
        setExistingThumbUrl(d.thumbnail_url ? String(d.thumbnail_url) : "");
        setActive(d.active !== undefined && d.active !== null ? (Number(d.active) ? 1 : 0) : 1);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load info video.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  const validate = () => {
    const errs = {};
    if (!String(title || "").trim()) errs.title = "Title is required";
    if (type === "Thirdparty" && !String(videoUrl || "").trim()) errs.video_url = "Video URL is required for Thirdparty type";
    if (type === "Selfhosted" && !isEdit && !videoFile) errs.video = "Please select a video file";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSave = async () => {
    if (saving) return;
    setError("");
    if (!validate()) return;

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", String(title || "").trim());
      fd.append("type", type);
      fd.append("active", String(active));
      fd.append("video_orientation", videoOrientation);
      if (type === "Thirdparty") fd.append("video_url", String(videoUrl || "").trim());
      if (type === "Selfhosted" && videoFile) fd.append("video", videoFile);
      if (thumbFile) fd.append("thumbnail", thumbFile);

      if (isEdit) {
        const res = await adminInfoVideoUpdateAction(id, fd);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Update failed");
        toast.success("Info video updated", { toastId: "info-video-save" });
      } else {
        const res = await adminInfoVideoCreateAction(fd);
        if (!res?.success) throw new Error(res?.message || res?.msg || "Create failed");
        toast.success("Info video created", { toastId: "info-video-save" });
      }
      navigate("/info-videos");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
      toast.error("Save failed", { toastId: "info-video-save" });
    } finally {
      setSaving(false);
    }
  };

  const currentVideoHref = (() => {
    if (!existingVideoUrl) return "";
    if (type === "Thirdparty") return existingVideoUrl;
    return resolvePublicUrl(existingVideoUrl);
  })();
  const currentThumbHref = existingThumbUrl ? resolvePublicUrl(existingThumbUrl) : "";

  return (
    <div className="content">
      {(loading || saving) && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      <div className="toolbar">
        <div className="breadcrumb">
          <Link to="/info-videos">Info Videos</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"} Info Video</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/info-videos" className="btn btn-primary">
            ← Back
          </Link>
        </div>
      </div>

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Save failed.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="grid-2">
        <div className="card">
          <div className="settings-section">
            <h3>Info Video Details</h3>

            <div className="form-group">
              <label>
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors((p) => ({ ...(p || {}), title: "" }));
                }}
                placeholder="Video title"
                style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.title ? <div className="text-danger field-error">{fieldErrors.title}</div> : null}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <select
                  className="form-control"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setFieldErrors((p) => ({ ...(p || {}), video: "", video_url: "" }));
                    if (e.target.value === "Thirdparty") setVideoFile(null);
                  }}
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Active</label>
                <select className="form-control" value={active} onChange={(e) => setActive(Number(e.target.value))}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="form-group">
                <label>Video Orientation</label>
                <select className="form-control" value={videoOrientation} onChange={(e) => setVideoOrientation(e.target.value)}>
                  {VIDEO_ORIENTATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {type === "Thirdparty" ? (
              <div className="form-group">
                <label>
                  Video URL <span className="text-danger">*</span>
                </label>
                <input
                  type="url"
                  className="form-control"
                  value={videoUrl}
                  onChange={(e) => {
                    setVideoUrl(e.target.value);
                    setFieldErrors((p) => ({ ...(p || {}), video_url: "" }));
                  }}
                  placeholder="https://..."
                  style={fieldErrors.video_url ? { borderColor: "var(--danger)" } : undefined}
                />
                {fieldErrors.video_url ? <div className="text-danger field-error">{fieldErrors.video_url}</div> : null}
                {isEdit && currentVideoHref ? (
                  <p className="small-text mt-2">
                    Current:{" "}
                    <a href={currentVideoHref} target="_blank" rel="noreferrer">
                      Open link
                    </a>
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="form-group">
                <label>
                  Video File {!isEdit ? <span className="text-danger">*</span> : null}
                </label>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setVideoFile(f);
                    setFieldErrors((p) => ({ ...(p || {}), video: "" }));
                  }}
                />
                <button className="btn btn-outline" type="button" onClick={() => videoInputRef.current && videoInputRef.current.click()}>
                  Choose Video
                </button>
                {videoFile ? <p className="small-text mt-2">Selected: {videoFile.name}</p> : null}
                {isEdit && !videoFile && currentVideoHref ? (
                  <p className="small-text mt-2">
                    Current:{" "}
                    <a href={currentVideoHref} target="_blank" rel="noreferrer">
                      Open video
                    </a>
                  </p>
                ) : null}
                {fieldErrors.video ? <div className="text-danger field-error">{fieldErrors.video}</div> : null}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="settings-section">
            <h2 className="section-title">Thumbnail Image</h2>
            <div
              className="upload-area p-sm"
              role="button"
              tabIndex={0}
              onClick={() => thumbInputRef.current?.click?.()}
              onKeyDown={(e) => e.key === "Enter" && thumbInputRef.current?.click?.()}
            >
              <div className="avatar-upload" style={{ overflow: "hidden" }}>
                {thumbPreviewUrl || currentThumbHref ? (
                  <img
                    src={thumbPreviewUrl || currentThumbHref}
                    alt="Thumbnail preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ color: "var(--primary)", display: "inline-flex" }} aria-hidden="true">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="9" cy="9" r="2.5" />
                      <path d="M3 16l5-5 4 4 3-3 6 6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className="font-sm">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p className="small-label">PNG, JPG up to 2MB</p>
            </div>
            <input
              ref={thumbInputRef}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setThumbFile(f);
              }}
            />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" type="button" onClick={onSave} disabled={saving || loading}>
          {saving ? (
            "Saving…"
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                <path d="M17 21v-8H7v8" />
                <path d="M7 3v5h8" />
              </svg>
              {isEdit ? "Update Info Video" : "Save Info Video"}
            </>
          )}
        </button>
        <Link to="/info-videos" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}

